import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  Optional,
  Self,
} from '@angular/core';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { Subscription } from 'rxjs';

// S133 a11y (WCAG 4.1.2): Kendo dropdownlist 7.x renders <li role="option"> but
// does NOT emit aria-posinset / aria-setsize, so NVDA can announce the wrong
// position (e.g. "1 of 2" for every option instead of "1 of 2" / "2 of 2").
//
// This directive stamps the correct aria-posinset (1-based) and aria-setsize on
// THIS dropdown's own options each time the popup opens. It is scoped strictly to
// this dropdown via the host element's aria-controls -> listbox id, so it never
// touches another dropdown's options. It only ADDS the two attributes Kendo omits;
// it changes no Kendo behaviour and no other roles/names.
//
// Usage: add `appKendoOptionPosinset` to a <kendo-dropdownlist>.
@Directive({
  selector: 'kendo-dropdownlist[appKendoOptionPosinset]',
})
export class KendoOptionPosinsetDirective implements AfterViewInit, OnDestroy {
  private subs: Subscription[] = [];

  constructor(
    private host: ElementRef<HTMLElement>,
    @Optional() @Self() private dropdown?: DropDownListComponent
  ) {}

  ngAfterViewInit(): void {
    if (!this.dropdown) {
      return;
    }
    // `open` fires as the popup opens; the option elements may not exist for a
    // frame or two, so poll a few frames until they render, then stamp.
    this.subs.push(this.dropdown.open.subscribe(() => this.stampWhenReady(0)));
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  private stampWhenReady(attempt: number): void {
    const options = this.getOptions();
    if (options.length) {
      const setsize = options.length;
      options.forEach((opt, index) => {
        opt.setAttribute('aria-setsize', String(setsize));
        opt.setAttribute('aria-posinset', String(index + 1));
      });
      return;
    }
    // Retry briefly while the popup renders, then give up safely (no-op).
    if (attempt < 10) {
      requestAnimationFrame(() => this.stampWhenReady(attempt + 1));
    }
  }

  // Resolve only THIS dropdown's option rows via aria-controls -> listbox id.
  private getOptions(): HTMLElement[] {
    const listId = this.host.nativeElement.getAttribute('aria-controls');
    if (!listId) {
      return [];
    }
    const listbox = document.getElementById(listId);
    if (!listbox) {
      return [];
    }
    return Array.from(
      listbox.querySelectorAll<HTMLElement>('[role="option"]')
    );
  }
}
